import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { resolveOptionLabel } from '../booleanOptions';

export interface SelectValueLabelProps {
  value: string | null | undefined;
  propertyType?: PropertyTypeSchema;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for select/dropdown values.
 * Looks up the label from propertyType.options if available.
 *
 * @example
 * <SelectValueLabel value="option_1" propertyType={statusSchema} />
 * // Renders: "Active" (from options[].label)
 */
export const SelectValueLabel: React.FC<SelectValueLabelProps> = ({
  value,
  propertyType,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  // Look up label from options; synthesize Yes/No for boolean fields with no explicit options
  const displayValue = resolveOptionLabel(propertyType, value);

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={truncate ? displayValue : undefined}
    >
      {displayValue}
    </span>
  );
};
